import IconCoin from "@common/components/icons/IconCoin";
import ErrorMessage from "@common/components/text/ErrorMessage";
import AccordionSection from "@common/components/thirdParty/mantine/accordion/AccordionSection";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText";
import TitleWithIcon from "@common/components/thirdParty/mantine/text/TitleWithIcon";
import { persistUTMQueryParamsToUrlObject } from "@common/utils/router";
import TextInfos from "@components/layout/TextInfos";
import { Anchor, Flex, Stack, Text } from "@mantine/core";
import { isMonthlyPlan } from "@stores/plans/utils";
import { canChangePlan, getPrettyPlanName } from "@stores/purchases/utils";
import { BILLING_METHOD_INVOICE_TYPE } from "@stores/session/constants";
import { SUBSCRIPTION_STATUS_CANCELLATION_PENDING } from "@stores/subscriptions/constants";
import { formatDate } from "@utils/date";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactElement } from "react";
import { purchasesStore } from "@stores/purchases";
import { IPurchaseCardProps } from "../../types";
import { isDevPlan, isEnterprisePlan } from "@common/utils/plan";

export default function SubscriptionSection({
  purchase,
  account,
  paymentMethod,
}: Pick<
  IPurchaseCardProps,
  "purchase" | "account" | "paymentMethod"
>): ReactElement {
  const router = useRouter();

  // purchase & subscription
  const { id, subscription, product, next_charge } = purchase;
  const { plan } = product;
  const { current_period_end, trial_end_days } = subscription;

  const isDev = isDevPlan(purchase.product.plan.alias);
  const isNotEnterprise = !isEnterprisePlan(purchase.product.plan.alias);

  // reactivate
  const {
    isPending: loadingReactivatePurchase,
    error: reactivatePurchaseError,
    mutate: reactivatePurchase,
  } = purchasesStore.useReactivatePurchase(id);

  return (
    <AccordionSection>
      <Flex align="center" direction="row" mb="sm">
        <TitleWithIcon
          icon={<IconCoin size={13} />}
          title="Subscription"
          order={3}
          size="h6"
        />
      </Flex>

      <Stack spacing="xs">
        {/* Trial */}
        {!!(trial_end_days && trial_end_days >= 0) && (
          <TextInfos text1="Trial" text2={`${trial_end_days} days left`} />
        )}

        {/* Next charge */}
        {next_charge && (
          <TextInfos
            text1="Next charge"
            text2={
              <>
                <b>{formatDate(next_charge.date, "long")}</b> for{" "}
                <b>{next_charge.amount_due}</b>
                {paymentMethod && (
                  <>
                    {" "}
                    on the <b>{paymentMethod.type}</b> ending in{" "}
                    <b>{paymentMethod.last4}</b>
                  </>
                )}
              </>
            }
            text3={
              <Anchor
                component={Link}
                href={persistUTMQueryParamsToUrlObject(
                  router,
                  `/account/manage/billing`,
                )}
              >
                Edit payment
              </Anchor>
            }
          />
        )}

        {/* Plan */}
        {isDev &&
          subscription.status === SUBSCRIPTION_STATUS_CANCELLATION_PENDING &&
          current_period_end && (
            <Text fz="sm">
              Your plan has been <b>canceled</b>
              .<br />
              You can continue to use your <b>
                {getPrettyPlanName(purchase)}
              </b>{" "}
              until <b>{formatDate(current_period_end, "long")}</b>.
            </Text>
          )}
        {!isDev && (
          <TextInfos
            text1="Plan"
            text2={
              subscription.status ===
                SUBSCRIPTION_STATUS_CANCELLATION_PENDING &&
              current_period_end ? (
                <>
                  Your plan has been <b>canceled</b>
                  .<br />
                  You can continue to use your{" "}
                  <b>{getPrettyPlanName(purchase)}</b> until{" "}
                  <b>{formatDate(current_period_end, "long")}</b>
                </>
              ) : (
                getPrettyPlanName(purchase)
              )
            }
            text3={
              isNotEnterprise &&
              (subscription.status ===
                SUBSCRIPTION_STATUS_CANCELLATION_PENDING &&
              current_period_end ? (
                <ButtonAsText
                  loading={loadingReactivatePurchase}
                  onClick={() => reactivatePurchase(null)}
                  fw={700}
                >
                  Reactivate
                </ButtonAsText>
              ) : (
                <Anchor
                  component={Link}
                  href={persistUTMQueryParamsToUrlObject(
                    router,
                    `/account/${id}/plan`,
                  )}
                >
                  Manage plan
                </Anchor>
              ))
            }
          />
        )}

        {/* Terms */}
        {!isDev && (
          <TextInfos
            text1="Terms"
            text2={isMonthlyPlan(plan) ? "Monthly" : "Yearly"}
            text3={
              account.billing_method !== BILLING_METHOD_INVOICE_TYPE &&
              canChangePlan(purchase) &&
              isMonthlyPlan(plan) && (
                <Anchor
                  component={Link}
                  href={persistUTMQueryParamsToUrlObject(
                    router,
                    `/account/${id}/billing`,
                  )}
                >
                  Save 10% with annual billing
                </Anchor>
              )
            }
          />
        )}

        <ErrorMessage error={reactivatePurchaseError} />
      </Stack>
    </AccordionSection>
  );
}
